import AppCard from '@nxsys/components/AppCard';
import AppIconCard from '@nxsys/components/AppIconCard';
import {
  PAYMENT_MODEL_ENUM,
  getIrelandPaymentModels,
} from '@nxsys/constants/AppConstants';
import { useGetStatisticsPayments } from '@nxsys/hooks/api/dashboard';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { toNumber } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PaymentFileModal from './components/PaymentFileModal';
import ConfirmPaymentModal from './components/ConfirmPaymentModal';
import FPSModal from './components/FPSModal';
import PSRModal, { PSRModalTypeEnum } from './components/PSRModal';

const Payments = () => {
  const { value, loading, run } = useGetStatisticsPayments();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  // Ireland's models depend on the product too: bureau has PAYE + RCT, umbrella
  // adds UMBRELLA. Hardcoding the bureau pair here filtered every Irish
  // umbrella payroll out of this surface's counts and lists.
  const validPaymentModels = isIreland
    ? getIrelandPaymentModels(isBureau)
    : isBureau
      ? [PAYMENT_MODEL_ENUM.PAYEE]
      : undefined;
  const data = useMemo(() => value?.data, [value]);
  const [isOpenPaymentFileModal, setIsOpenPaymentFileModal] = useState(false);
  const [isOpenConfirmPaymentModal, setIsOpenConfirmPaymentModal] =
    useState(false);
  // Answering Yes to the "submit FPS/PSR?" prompt after confirming a payment
  // opens the submission screen from here, so it works on the employer
  // dashboard too - that one does not render the RTI/Revenue widgets.
  const [isOpenSubmitFpsPsrModal, setIsOpenSubmitFpsPsrModal] = useState(false);

  useEffect(() => {
    run({ paymentModels: validPaymentModels });
  }, []);

  useEffect(() => {
    const handleReload = () => run({ paymentModels: validPaymentModels });
    window.addEventListener('dashboard:reload-payments', handleReload);
    return () =>
      window.removeEventListener('dashboard:reload-payments', handleReload);
  }, []);

  const items = [
    {
      title: !isBureau
        ? formatMoney(
            toNumber(data?.paymentOutstandingTotal),
            showCurrencyByCountry(payrollCountry),
          )
        : data?.paymentOutstandingRecords,
      description: 'Download Bank File',
      icon: <Icons.IconMoneyReceive />,
      bgIconColor: 'bg-success-100',
      onClick: () => setIsOpenPaymentFileModal(true),
    },
    {
      title: !isBureau
        ? formatMoney(
            toNumber(data?.paymentPendingTotal),
            showCurrencyByCountry(payrollCountry),
          )
        : data?.paymentPendingRecords,
      description: isBureau ? 'Confirm Payment' : 'Payment Pending',
      icon: <Icons.IconMoneyBillMinus />,
      bgIconColor: 'bg-warning-100',
      onClick: () => setIsOpenConfirmPaymentModal(true),
    },
  ];

  return (
    <div className="Payments h-full">
      <AppCard className="p-[24px] h-full" loading={loading}>
        <div className="flex justify-between items-center mb-[24px]">
          <AppIconCard iconName="IconMoneyBill" title="Payments" />
        </div>
        <div className="grid grid-cols-2 gap-[16px] max-sm:grid-cols-1">
          {items.map((item) => (
            <div
              key={item.title}
              className="border border-[#CDDDD9] dashboard-hover-card bg-[#F9FBFA] rounded-[8px] p-[16px]"
              onClick={item.onClick}
            >
              <div className="flex items-center justify-between gap-x-[8px] mb-[16px]">
                <div className={`${item.bgIconColor} rounded-[8px] p-[8px]`}>
                  {item.icon}
                </div>
                <Icons.IconArrowRight />
              </div>
              <div className="text-[22px] font-bold mb-[8px]">{item.title}</div>
              <div className="text-[14px] font-bold">{item.description}</div>
            </div>
          ))}
        </div>
      </AppCard>
      <PaymentFileModal
        isOpen={isOpenPaymentFileModal}
        toggle={() => setIsOpenPaymentFileModal(false)}
        cbAfterAction={() => run({ paymentModels: validPaymentModels })}
      />
      <ConfirmPaymentModal
        isOpen={isOpenConfirmPaymentModal}
        toggle={() => setIsOpenConfirmPaymentModal(false)}
        cbAfterAction={() => run({ paymentModels: validPaymentModels })}
        onSubmitFpsPsr={() => setIsOpenSubmitFpsPsrModal(true)}
      />
      {isIreland ? (
        <PSRModal
          isOpen={isOpenSubmitFpsPsrModal}
          toggle={() => setIsOpenSubmitFpsPsrModal(false)}
          type={PSRModalTypeEnum.SUBMIT_PSR}
          cbAfterAction={() => run({ paymentModels: validPaymentModels })}
        />
      ) : (
        <FPSModal
          isOpen={isOpenSubmitFpsPsrModal}
          toggle={() => setIsOpenSubmitFpsPsrModal(false)}
        />
      )}
    </div>
  );
};

export default Payments;
